import React from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

export type FormTextFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  'name' | 'defaultValue'
> & {
  name: FieldPath<T>;
  control: Control<T>;
  large?: boolean;
  signedNumericOnly?: boolean;
  prefix?: string;
  suffix?: string;
};

const SIGNED_DECIMAL_RE = /^-?\d*(\.\d*)?$/;

function FormTextFieldInner<T extends FieldValues>(
  {
    name,
    control,
    helperText,
    large = false,
    signedNumericOnly = false,
    prefix = '',
    suffix = '',
    ...textProps
  }: FormTextFieldProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const error = !!fieldState.error;

        const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
          const next = e.target.value ?? '';
          if (signedNumericOnly && !SIGNED_DECIMAL_RE.test(next)) return;
          field.onChange(e);
        };

        return (
          <TextField
            {...textProps}
            {...field}
            variant="outlined"
            size={large ? 'medium' : 'small'}
            multiline={large || textProps.multiline}
            rows={large ? 4 : textProps.rows}
            rowsMax={large ? 8 : textProps.rowsMax}
            inputRef={(node) => {
              if (typeof ref === 'function') ref(node);
              else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
              field.ref(node);
            }}
            onChange={handleChange}
            inputProps={{
              ...(textProps.inputProps || {}),
              inputMode: signedNumericOnly ? 'numeric' : textProps.inputProps?.inputMode,
              pattern: signedNumericOnly ? '^-?\\d*$' : textProps.inputProps?.pattern,
            }}
            InputProps={{
              ...(textProps.InputProps || {}),
              startAdornment: prefix ? (
                <InputAdornment position="start">{prefix}</InputAdornment>
              ) : (
                textProps.InputProps?.startAdornment
              ),
              endAdornment: suffix ? (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ) : (
                textProps.InputProps?.endAdornment
              ),
            }}
            error={error}
          />
        );
      }}
    />
  );
}

const FormTextField = React.forwardRef(FormTextFieldInner) as <T extends FieldValues>(
  props: FormTextFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => ReturnType<typeof FormTextFieldInner>;

export default FormTextField;
